// The bodies the observer can see and fly between: a registry in the universe's own terms, and where the observer stands among them (observer-centric LOD, step 7).
//
// Nothing here is special to "the world". A body is a sphere in universe space (a star, a rocky moon, a planet with an air) with a position, a radius, a spin axis and what it does to light.
// The observer is a point among them. Everything the picture needs about a body (where it is, how big it looks, which way its light comes from, what it is lit with) is worked out
// from the two, in doubles, relative to the observer, so a body 1e11 m away and one 1e6 m away are treated alike and nothing is lost to the float32 the shaders get.
//
// THE STUB. The server sends today what this registry holds for one world: the sun message has the world's universe position, its local frame (east, north, up in universe space), the
// sun's and the moon's directions and sizes in that frame. `feedSun` builds the registry from that message and from three constants the message does not carry yet (the planet's
// radius, which is the renderer's slider; the moon's radius and albedo, which are the server's config's defaults; the star's luminosity and colour). It is a stub for a later
// change: when the server publishes its bodies as a list, `feedSun` is replaced by a reader of that list and nothing else in this file or the shaders changes. Every place that
// stands for something the server will one day say is marked STUB.
//
// UNITS. Universe space is in units of UNIT_M metres (the server's orbit radius is 1e4 of them, an astronomical unit). Positions in this file are in METRES, relative to the home
// planet's centre (the absolute universe position of the world is 1e4 units out; subtracting it once, in doubles, keeps every later number small and exact).
//
// FRAMES. A "pose" is the observer's place and the axes the picture is drawn in: render axes x east, y up, z minus north (three's convention, as everywhere else), expressed as
// three rows in universe axes. Near the home planet (the tile regime) the axes are the world's chart: the flat tile is laid on the sphere by the azimuthal-equidistant map about the
// world's own point (an east/north offset on the tile is an arc on the sphere), and the axes are the world's frame carried along the geodesic to the observer, so at the world's
// own point they are exactly the server's frame and the sun is where the server says. Elsewhere (the free regime) they are the nearest body's own local frame: up away from its centre,
// east = spin axis x up, which does not turn with time, so a heading held in it is held against the stars.

export type V3 = [number, number, number];
/** Row-major 3x3: three rows of three. */
export type M3 = [number, number, number, number, number, number, number, number, number];

/** Metres in one universe unit: the server's orbit radius is 1e4 units and the real one 1.496e11 m. STUB (a constant of the universe the server will state). */
export const UNIT_M = 1.496e7;

/** The tile regime ends and the free regime begins this high above the home planet's sea level (m): above the near field's cross-fade (nearFade), where the tile is a speck. */
export const FREE_ABOVE_M = 150e3;
/** ... and the free regime hands back to the tile below this (m): lower than the other by a margin, so the switch does not flutter on its edge. */
export const TILE_BELOW_M = 135e3;
/** The near field (the flat tile's sea, land, cloud volume) is drawn in full below NEAR_FULL_M above the sea and not at all above NEAR_GONE_M (m). */
export const NEAR_FULL_M = 40e3;
export const NEAR_GONE_M = 120e3;
/** The cloud volume's own limit: the flat tile's camera frame holds out to FREE_ABOVE_M, and the volume cannot be drawn in the free frame, so it is faded out over the last stretch of it. Below
 *  that what the volume shows and what the world painter's shell shows is settled pixel by pixel (handover.ts), not by height. */
export const CLOUD_FULL_M = 95e3;
export const CLOUD_GONE_M = 125e3;
/** A body takes over as the one the picture follows when its surface is this share of the current one's distance away (hysteresis). */
export const SWITCH_RATIO = 0.8;

const D2R = Math.PI / 180;

// ── small vector maths (doubles, plain arrays) ──────────────────────────────────────────────────────────────────────────────────────────────────
export const dot = (a: ArrayLike<number>, b: ArrayLike<number>): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
export const cross = (a: ArrayLike<number>, b: ArrayLike<number>): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
export const len = (a: ArrayLike<number>): number => Math.hypot(a[0], a[1], a[2]);
export const sub = (a: ArrayLike<number>, b: ArrayLike<number>): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
export const add = (a: ArrayLike<number>, b: ArrayLike<number>): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
export const scale = (a: ArrayLike<number>, s: number): V3 => [a[0] * s, a[1] * s, a[2] * s];
export const norm = (a: ArrayLike<number>): V3 => { const n = len(a); return n > 0 ? [a[0] / n, a[1] / n, a[2] / n] : [0, 0, 1]; };
/** v rotated about the unit axis k by angle th (Rodrigues). */
export function rotate(v: ArrayLike<number>, k: ArrayLike<number>, th: number): V3 {
  const c = Math.cos(th), s = Math.sin(th), kv = dot(k, v), kxv = cross(k, v);
  return [v[0] * c + kxv[0] * s + k[0] * kv * (1 - c), v[1] * c + kxv[1] * s + k[1] * kv * (1 - c), v[2] * c + kxv[2] * s + k[2] * kv * (1 - c)];
}
/** The row-major matrix with these three rows. */
export const rows = (a: ArrayLike<number>, b: ArrayLike<number>, c: ArrayLike<number>): M3 => [a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]];
/** m * v. */
export const mulv = (m: ArrayLike<number>, v: ArrayLike<number>): V3 => [m[0] * v[0] + m[1] * v[1] + m[2] * v[2], m[3] * v[0] + m[4] * v[1] + m[5] * v[2], m[6] * v[0] + m[7] * v[1] + m[8] * v[2]];
/** m^T * v (the rows' combination: the inverse of mulv for an orthonormal m). */
export const mulvT = (m: ArrayLike<number>, v: ArrayLike<number>): V3 => [m[0] * v[0] + m[3] * v[1] + m[6] * v[2], m[1] * v[0] + m[4] * v[1] + m[7] * v[2], m[2] * v[0] + m[5] * v[1] + m[8] * v[2]];

// ── bodies ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
/**
 * star: gives light (lum, rgb); not drawn by the far shell (the sun's disc and glare are the sky's own, sky.ts and sunGlare.ts).
 * rock: an airless body with a surface map (the moon).
 * world: a planet with an air, an ocean and a cloud cover.
 */
export type BodyKind = 'star' | 'rock' | 'world';

export interface Body {
  id: string;
  kind: BodyKind;
  /** Centre, metres, relative to the home planet's centre, universe axes. */
  pos: V3;
  /** Radius, metres. For the home planet: the sea level. */
  radius: number;
  /** Spin axis (unit, universe axes). */
  axis: V3;
  /** The body-fixed frame (rows: the axes of its map, in universe axes) for a rock with a surface map: x right, y north, z toward the near side. */
  fixed: M3;
  /** Mean albedo, per channel (a rock) / the colour of its light (a star). */
  rgb: V3;
  /** A star's luminosity in the world's units (its flux is lum / d^2, d in universe units, 1 at the world). 0 for what does not shine. */
  lum: number;
  /** The server's GM (m^3 per tick^2): what the body pulls with. Undefined for the stub (an old server). */
  gm?: number;
}

/** A circle a body moves on about its parent (bodies.py): metres, radians, ticks. */
export interface Circle { a: number; e1: V3; e2: V3; w: number; phi0: number; }

/** The server's bodies message (bodies.py), as parsed. */
export interface BodiesMsg {
  type: 'bodies';
  version: number;
  unit_m: number;
  seconds_per_tick: number;
  bodies: {
    id: string; kind: BodyKind; parent: string | null; gm: number; radius_m: number;
    pos?: ArrayLike<number>;                                               // a star: universe units
    orbit?: { a: number; e1: ArrayLike<number>; e2: ArrayLike<number>; w: number; phi0: number };   // a: universe units
    rgb?: ArrayLike<number>; lum?: number;
  }[];
}

interface Ephem { id: string; parent: string | null; circle: Circle | null; pos0: V3 | null; gm: number; radius: number; kind: BodyKind; rgb: V3; lum: number; }

/** What the registry is fed from today (the sun message, parsed). */
export interface SunFeed {
  worldPos: ArrayLike<number>;              // the world's universe position (units)
  frame: ArrayLike<number>;                 // 3x3 row-major: east, north, up in universe space
  direction: ArrayLike<number>;             // toward the star, in the world's local frame (x east, y north, z up)
  angularDiameter: number;                  // the star's, radians
  flux: number;                             // the star's flux at the world, outside the air
  moon?: { direction: ArrayLike<number>; angularDiameter: number } | null;
}

// STUB: what the sun message does not say yet.
const STAR_LUMINOSITY = 1e8;                 // world flux 1 at an orbit radius of 1e4 units
const STAR_RGB: V3 = [1.0, 0.98, 0.92];
const MOON_RADIUS_M = 0.116 * UNIT_M;
const MOON_ALBEDO: V3 = [0.125, 0.12, 0.11];
const MOON_INCLINATION = 5.1 * D2R;          // of its orbit about the universe x axis (world_server.py)
const DEFAULT_LATITUDE_DEG = 18;
const DEFAULT_RADIUS_M = 6.371e6;

/** The chart that lays the flat tile on the home planet: the world's own point, its frame, the radius. */
interface Chart {
  e0: V3; n0: V3; u0: V3;                    // east, north, up at the world's point (universe axes)
  R: number;                                 // metres
}

export interface Pose {
  /** Index in `list` of the body the picture follows. */
  body: number;
  /** Height above that body's sea level (m). */
  h: number;
  /** The observer, metres, relative to the home planet's centre (universe axes). */
  pos: V3;
  /** The observer relative to the followed body's centre (m, universe axes). */
  rel: V3;
  /** Rows: render axes x (east), y (up), z (minus north) as vectors in universe axes. A universe vector v is in render axes as (row0.v, row1.v, row2.v). */
  axes: M3;
  /** Rows east, north, up (the sim's local frame, what the star shaders take). */
  local: M3;
  /** Whether the axes are the home tile's chart (true) or the followed body's own frame (false). */
  tile: boolean;
}

/** What the far shell draws for one body: everything in render axes, relative to the observer, in metres. */
export interface FarBody {
  id: string;
  kind: BodyKind;
  c: V3;                                     // centre
  r: number;                                 // radius
  lightDir: V3;                              // toward the star that lights it (unit)
  flux: V3;                                  // the light on it, per channel, in the world's flux unit
  fixed: M3;                                 // body-fixed rows, in render axes
  /** A rock: disc-average surface brightness in the sky's units (flux * albedo * 2/3 * phase function), and the disc-average of the Lommel-Seeliger term for this phase. */
  mean: V3;
  meanShade: number;
  /** The phase angle seen from the observer (star-body-observer), radians. */
  phase: number;
  /** The angular radius the observer sees (rad). */
  ang: number;
  /** Distance from the observer to the centre (m). */
  dist: number;
}

/** Disc-average of the Lommel-Seeliger term over the visible disc at phase angle `phase` (moon.ts's quadrature). */
export function meanShade(phase: number): number {
  const NM = 10, NP = 24;
  const sp = Math.sin(phase), cp = Math.cos(phase);
  let sum = 0, wsum = 0;
  for (let a = 0; a < NM; a++) {
    const mu = (a + 0.5) / NM, st = Math.sqrt(1 - mu * mu);
    for (let b = 0; b < NP; b++) {
      const ci = st * Math.cos((b + 0.5) * 2 * Math.PI / NP) * sp + mu * cp;
      sum += (ci > 0 ? ci / (ci + mu) : 0) * mu;
      wsum += mu;
    }
  }
  return sum / wsum;
}

/** The phase function of a Lambert sphere (the server's moon formula uses it): 1 at full, 0 at new. */
export const lambertPhase = (alpha: number): number => (Math.sin(alpha) + (Math.PI - alpha) * Math.cos(alpha)) / Math.PI;

const smooth = (a: number, b: number, x: number): number => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

/** How much of the near field (the flat tile) is drawn at height h above the home planet's sea level: 1 below NEAR_FULL_M, 0 above NEAR_GONE_M. */
export function nearFade(h: number): number { return 1 - smooth(NEAR_FULL_M, NEAR_GONE_M, h); }
/** How much of the cloud volume is drawn at height h: 1 below CLOUD_FULL_M, 0 above CLOUD_GONE_M. */
export function cloudFade(h: number): number { return 1 - smooth(CLOUD_FULL_M, CLOUD_GONE_M, h); }

export class Bodies {
  readonly list: Body[] = [];
  /** Incremented whenever the set or the planet's radius changes (a consumer that caches something derived from them compares). */
  version = 0;
  private chart: Chart | null = null;
  private haveFeed = false;
  private latDeg = DEFAULT_LATITUDE_DEG;
  private radiusM = DEFAULT_RADIUS_M;
  private world: V3 = [0, 0, 0];
  /** The world's absolute universe position (units): the stars' shaders subtract star positions from it. */
  worldAbs: V3 = [0, 0, 0];
  /** Metres in one universe unit, and seconds in one tick: the server's (the bodies message), else the stub's. */
  unitM = UNIT_M;
  secPerTick = 0.02;
  private eph = new Map<string, Ephem>();
  /** The ephemeris' children of each body (ids), and the mass (GM) of each body together with everything that goes round it. */
  private kids = new Map<string, string[]>();
  private msub = new Map<string, number>();
  private stateMemo: { tick: number; pos: Map<string, V3>; vel: Map<string, V3> } | null = null;
  private useEph = false;
  /** Every body's position at the tick the registry was last fed with (`feedEphemeris`), in the star's frame (the star at the origin; metres; universe axes), in `list` order. */
  absNow: V3[] = [];
  /** The tick of the last `feedEphemeris`. */
  tickNow = NaN;
  /** The air's zenith optical depth per channel (the world's atmosphere, from the stars message), for the shaders. STUB until the server states the air of each body. */
  tau: V3 = [0.05, 0.1, 0.22];

  /** The planet's radius (m): the renderer's slider. */
  setRadius(m: number): void {
    if (!(m > 0) || m === this.radiusM) return;
    this.radiusM = m;
    this.version++;
    if (this.haveFeed) this.rebuildHome();
  }
  get radius(): number { return this.radiusM; }
  /** The world's latitude (deg): sets the spin axis the free regime's east/north are taken from. */
  setLatitude(deg: number): void {
    if (!Number.isFinite(deg) || deg === this.latDeg) return;
    this.latDeg = deg;
    if (this.haveFeed) this.rebuildHome();
  }
  setAir(tau: ArrayLike<number> | null | undefined): void {
    if (tau && tau.length >= 3 && tau[0] >= 0) this.tau = [tau[0], tau[1], tau[2]];
  }
  get ready(): boolean { return this.haveFeed; }
  get(id: string): Body | undefined { return this.list.find(b => b.id === id); }
  indexOf(id: string): number { return this.list.findIndex(b => b.id === id); }
  get home(): Body | undefined { return this.list.length ? this.list[0] : undefined; }

  private set(b: Body): void {
    const i = this.indexOf(b.id);
    if (i >= 0) this.list[i] = b; else { this.list.push(b); this.version++; }
  }

  private rebuildHome(): void {
    const c = this.chart;
    if (!c) return;
    c.R = this.radiusM;
    this.world = scale(c.u0, this.radiusM);
    const lat = this.latDeg * D2R;
    const axis = norm(add(scale(c.u0, Math.sin(lat)), scale(c.n0, Math.cos(lat))));
    const h = this.get('home');
    const b: Body = { id: 'home', kind: 'world', pos: [0, 0, 0], radius: this.radiusM, axis, fixed: rows(c.e0, c.n0, c.u0), rgb: [1, 1, 1], lum: 0 };
    if (h) { this.list[this.indexOf('home')] = b; } else this.list.unshift(b);
  }

  /** STUB: build the registry from the sun message. */
  feedSun(f: SunFeed): void {
    const fr = f.frame;
    if (!fr || fr.length < 9 || !f.worldPos) return;
    // the frame as it comes may be a little off an orthonormal one (the renderer extrapolates it between messages): east and up are kept, north is made to complete them
    const e1 = norm([fr[0], fr[1], fr[2]]), u1 = norm([fr[6], fr[7], fr[8]]);
    const e0 = norm(sub(e1, scale(u1, dot(e1, u1)))), u0 = u1, n0 = cross(u0, e0);
    this.chart = { e0, n0, u0, R: this.radiusM };
    this.worldAbs = [f.worldPos[0], f.worldPos[1], f.worldPos[2]];
    // The world's point is on the planet's surface (the server computes its sky from there), so the centre is one radius below it. Positions are relative to that centre.
    this.world = scale(u0, this.radiusM);
    const had = this.haveFeed;
    this.haveFeed = true;
    this.rebuildHome();
    if (!had) this.version++;
    if (this.useEph) return;       // the star and the moon are the server's (feedEphemeris)
    const toUniv = (d: ArrayLike<number>): V3 => norm(add(add(scale(e0, d[0]), scale(n0, d[1])), scale(u0, d[2])));   // local -> universe: the frame's transpose
    // the star: where the server says, at the distance its flux and luminosity imply (flux = lum / d^2, d in units)
    const dStar = Math.sqrt(STAR_LUMINOSITY / Math.max(f.flux, 1e-30)) * UNIT_M;
    const sDir = toUniv(f.direction);
    this.set({ id: 'sun', kind: 'star', pos: add(this.world, scale(sDir, dStar)), radius: dStar * Math.tan(0.5 * f.angularDiameter), axis: [0, 0, 1], fixed: rows([1, 0, 0], [0, 1, 0], [0, 0, 1]), rgb: STAR_RGB, lum: STAR_LUMINOSITY });
    if (f.moon && f.moon.angularDiameter > 0) {
      const mDir = toUniv(f.moon.direction);
      const dMoon = MOON_RADIUS_M / Math.tan(0.5 * f.moon.angularDiameter);
      const pos = add(this.world, scale(mDir, dMoon));
      // tidally locked: the near side faces the planet; north is the orbit's normal (the orbit is tilted about the universe x axis; STUB)
      const nrm: V3 = [0, -Math.sin(MOON_INCLINATION), Math.cos(MOON_INCLINATION)];
      const toward = norm(scale(pos, -1));                          // from the moon toward the planet's centre
      const north = norm(sub(nrm, scale(toward, dot(nrm, toward))));
      const east = cross(north, toward);
      this.set({ id: 'moon', kind: 'rock', pos, radius: MOON_RADIUS_M, axis: nrm, fixed: rows(east, north, toward), rgb: MOON_ALBEDO, lum: 0 });
    } else {
      const i = this.indexOf('moon');
      if (i >= 0) { this.list.splice(i, 1); this.version++; }
    }
  }

  // ── the server's bodies: circles, masses ────────────────────────────────────────────────────────────────────────────────────────────────────
  /** Take the server's bodies message (null: forget it, the registry is the stub again). The positions come with `feedEphemeris`. */
  setEphemeris(msg: BodiesMsg | null): void {
    this.eph.clear();
    this.kids.clear(); this.msub.clear(); this.stateMemo = null;
    this.useEph = false;
    this.absNow = [];
    this.tickNow = NaN;
    if (!msg || !msg.bodies || msg.bodies.length === 0 || !(msg.unit_m > 0)) return;
    const u = msg.unit_m;
    let star: Ephem | null = null;
    for (const b of msg.bodies) {
      const e: Ephem = {
        id: b.id, parent: b.parent, kind: b.kind, gm: b.gm > 0 ? b.gm : 0, radius: b.radius_m,
        circle: b.orbit ? { a: b.orbit.a * u, e1: norm([b.orbit.e1[0], b.orbit.e1[1], b.orbit.e1[2]]), e2: norm([b.orbit.e2[0], b.orbit.e2[1], b.orbit.e2[2]]), w: b.orbit.w, phi0: b.orbit.phi0 } : null,
        pos0: null, rgb: b.rgb && b.rgb.length >= 3 ? [b.rgb[0], b.rgb[1], b.rgb[2]] : [1, 1, 1], lum: b.lum && b.lum > 0 ? b.lum : 0,
      };
      if (!e.circle && b.pos) { e.pos0 = [b.pos[0] * u, b.pos[1] * u, b.pos[2] * u]; if (!star) star = e; }
      this.eph.set(b.id, e);
    }
    if (!star || !this.eph.has('home')) { this.eph.clear(); return; }      // a list this registry cannot make sense of (no star, or no home world): the stub stays
    const s0 = star.pos0 as V3;                                             // the star's frame: its origin is the first star (any other fixed one is placed from it)
    for (const e of this.eph.values()) if (e.pos0) e.pos0 = sub(e.pos0, s0);
    this.unitM = u;
    this.secPerTick = msg.seconds_per_tick > 0 ? msg.seconds_per_tick : 0.02;
    for (const e of this.eph.values()) if (e.parent && this.eph.has(e.parent)) { const k = this.kids.get(e.parent); if (k) k.push(e.id); else this.kids.set(e.parent, [e.id]); }
    const mass = (id: string, depth: number): number => {
      const e = this.eph.get(id);
      if (!e || depth > 16) return 0;
      let m = e.gm;
      for (const c of this.kids.get(id) ?? []) m += mass(c, depth + 1);
      this.msub.set(id, m);
      return m;
    };
    for (const e of this.eph.values()) if (e.pos0) mass(e.id, 0);
    this.useEph = true;
  }
  /** The mass (GM, m^3 per tick^2) of body `i` and of everything that goes round it (its moons, theirs...). */
  subtreeGm(i: number): number { const b = this.list[i]; return b ? this.msub.get(b.id) ?? b.gm ?? 0 : 0; }
  get hasEphemeris(): boolean { return this.useEph; }

  /**
   * Where the bodies are at `tick`, and how fast they move (the star's frame, metres, m per tick). The server puts each body on a circle about its parent and says nothing of the
   * parent's answer; in a real pair both go round their common centre. The circles are taken as the separation of the pair (parent body to the child with its own moons), and the centre of
   * mass of the pair is the parent's place on its own circle, as the server states it: the parent's body is then a little behind its circle (the sun by 450 km, the earth by up to 4700 km,
   * about the earth and moon's centre) and the child's a little ahead. The pair's separation is the server's exactly (the moon is where the server puts it, from the earth), only the
   * pair's place is moved. This is what makes the equilateral Lagrange points places where something stays (gravity.ts): with the parent pinned they are not.
   */
  private state(tick: number): { pos: Map<string, V3>; vel: Map<string, V3> } {
    const m = this.stateMemo;
    if (m && m.tick === tick) return m;
    const pos = new Map<string, V3>(), vel = new Map<string, V3>();
    const place = (id: string, bary: V3, bvel: V3, depth: number): void => {
      const e = this.eph.get(id);
      if (!e || depth > 16) return;
      const kids = this.kids.get(id) ?? [];
      const mt = this.msub.get(id) ?? 0;
      let px = bary[0], py = bary[1], pz = bary[2], vx = bvel[0], vy = bvel[1], vz = bvel[2];
      const seps: { id: string; s: V3; v: V3 }[] = [];
      for (const k of kids) {
        const ke = this.eph.get(k);
        if (!ke || !ke.circle) continue;
        const c = ke.circle, ph = c.phi0 + c.w * tick, cs = Math.cos(ph), sn = Math.sin(ph);
        const sv: V3 = [c.a * (cs * c.e1[0] + sn * c.e2[0]), c.a * (cs * c.e1[1] + sn * c.e2[1]), c.a * (cs * c.e1[2] + sn * c.e2[2])];
        const vv: V3 = [c.a * c.w * (-sn * c.e1[0] + cs * c.e2[0]), c.a * c.w * (-sn * c.e1[1] + cs * c.e2[1]), c.a * c.w * (-sn * c.e1[2] + cs * c.e2[2])];
        seps.push({ id: k, s: sv, v: vv });
        const f = mt > 0 ? (this.msub.get(k) ?? 0) / mt : 0;
        px -= f * sv[0]; py -= f * sv[1]; pz -= f * sv[2]; vx -= f * vv[0]; vy -= f * vv[1]; vz -= f * vv[2];
      }
      pos.set(id, [px, py, pz]); vel.set(id, [vx, vy, vz]);
      for (const k of seps) place(k.id, [px + k.s[0], py + k.s[1], pz + k.s[2]], [vx + k.v[0], vy + k.v[1], vz + k.v[2]], depth + 1);
    };
    for (const e of this.eph.values()) if (e.pos0) place(e.id, e.pos0, [0, 0, 0], 0);
    const out = { tick, pos, vel };
    this.stateMemo = out;
    return out;
  }

  /** Every body's position at `tick` in the star's frame (metres), in `list` order. */
  absAll(tick: number): V3[] {
    const st = this.state(tick).pos;
    return this.list.map(b => { const p = st.get(b.id); return p ? [p[0], p[1], p[2]] as V3 : [0, 0, 0] as V3; });
  }

  /** Body `i`'s velocity at `tick` in the star's frame (m per tick). */
  absVel(i: number, tick: number): V3 {
    const b = this.list[i];
    const v = b ? this.state(tick).vel.get(b.id) : undefined;
    return v ? [v[0], v[1], v[2]] : [0, 0, 0];
  }

  /** The circle body `i` moves on about its parent, and the parent's index in `list` (null for a body that does not move on a circle). */
  circleOf(i: number): { circle: Circle; parent: number } | null {
    const b = this.list[i];
    const e = b ? this.eph.get(b.id) : undefined;
    if (!e || !e.circle || !e.parent) return null;
    const pi = this.indexOf(e.parent);
    return pi >= 0 ? { circle: e.circle, parent: pi } : null;
  }

  /**
   * The registry at `tick`: the star and the moon (and whatever else the server lists) where the server's circles put them, relative to the home planet's centre. `bias` (0 to 1) is how much
   * of the world's own offset (the tile's point, a planet's radius from the centre) is added to every body but the home planet: 1 on the ground, where the sun message's directions are
   * those of the world's point and the sky must agree with them; 0 in space, where the positions are the circles' exactly.
   */
  feedEphemeris(tick: number, bias: number): void {
    if (!this.useEph || !this.haveFeed || !Number.isFinite(tick)) return;
    for (const e of this.eph.values()) {
      if (e.id === 'home' || this.indexOf(e.id) >= 0) continue;
      const star = e.kind === 'star';
      this.set({ id: e.id, kind: e.kind, pos: [0, 0, 0], radius: e.radius, axis: [0, 0, 1], fixed: rows([1, 0, 0], [0, 1, 0], [0, 0, 1]), rgb: star ? e.rgb : MOON_ALBEDO, lum: e.lum, gm: e.gm });
    }
    const abs = this.absAll(tick);
    this.absNow = abs;
    this.tickNow = tick;
    const h = abs[0];
    for (let i = 0; i < this.list.length; i++) {
      const b = this.list[i], e = this.eph.get(b.id);
      if (e) b.gm = e.gm;
      if (i === 0) { b.pos = [0, 0, 0]; continue; }
      if (!e) continue;
      b.radius = e.radius;
      b.pos = add(sub(abs[i], h), scale(this.world, bias));
      if (e.kind === 'rock' && e.circle && e.parent) {
        // tidally locked to its parent (near side toward it); north is its orbit's normal
        const pi = this.indexOf(e.parent), par = pi >= 0 ? abs[pi] : h;
        const nrm = norm(cross(e.circle.e1, e.circle.e2));
        const toward = norm(sub(par, abs[i]));
        const north = norm(sub(nrm, scale(toward, dot(nrm, toward))));
        b.axis = nrm;
        b.fixed = rows(cross(north, toward), north, toward);
      }
    }
  }

  // ── the chart: the flat tile on the home planet ──────────────────────────────────────────────────────────────────────────────────────────
  /** The tile's point (east and north offsets from the world's point, m; height above sea level, m) as an observer pose on the home planet. */
  tilePose(east: number, north: number, h: number): Pose | null {
    const c = this.chart;
    if (!c || !this.haveFeed) return null;
    const R = c.R, r = Math.hypot(east, north), th = Math.min(r / R, Math.PI - 1e-3);
    let up = c.u0, e = c.e0, n = c.n0;
    if (r > 1e-9) {
      const t = norm(add(scale(c.e0, east), scale(c.n0, north)));
      const k = norm(cross(c.u0, t));
      up = add(scale(c.u0, Math.cos(th)), scale(t, Math.sin(th)));
      e = rotate(c.e0, k, th); n = rotate(c.n0, k, th);
    }
    const pos = scale(up, R + h);
    return this.makePose(0, pos, pos, rows(e, up, scale(n, -1)), rows(e, n, up), true);
  }

  /** The tile coordinates (east, north offsets from the world's point, m; height above the sea, m) of an observer position relative to the home centre. */
  tileOf(pos: ArrayLike<number>): { east: number; north: number; h: number } | null {
    const c = this.chart;
    if (!c) return null;
    const w = len(pos), up = scale(pos, 1 / Math.max(w, 1e-9));
    const cosT = Math.min(1, Math.max(-1, dot(up, c.u0)));
    const sinT = len(cross(up, c.u0));
    const th = Math.atan2(sinT, cosT);
    let east = 0, north = 0;
    if (sinT > 1e-12) {
      const t = scale(sub(up, scale(c.u0, cosT)), 1 / sinT);
      const arc = c.R * th;
      east = arc * dot(t, c.e0); north = arc * dot(t, c.n0);
    }
    return { east, north, h: w - c.R };
  }

  /** The axes the free regime uses at `rel` (a position relative to the body's centre): up away from it, east = spin axis x up (does not turn with time), north completing. */
  private freeAxes(b: Body, rel: V3): { axes: M3; local: M3 } {
    const up = norm(rel);
    let e = cross(b.axis, up);
    if (len(e) < 1e-9) e = cross([1, 0, 0], up);
    e = norm(e);
    const n = cross(up, e);
    return { axes: rows(e, up, scale(n, -1)), local: rows(e, n, up) };
  }

  /** A free-regime pose: the observer at `rel` (metres, universe axes) from the centre of body `bi`. */
  freePose(bi: number, rel: ArrayLike<number>): Pose | null {
    const b = this.list[bi];
    if (!b) return null;
    const pos = add(b.pos, [rel[0], rel[1], rel[2]]);
    const fa = this.freeAxes(b, [rel[0], rel[1], rel[2]]);
    return this.makePose(bi, pos, [rel[0], rel[1], rel[2]], fa.axes, fa.local, false);
  }

  private makePose(bi: number, pos: V3, rel: V3, axes: M3, local: M3, tile: boolean): Pose {
    const b = this.list[bi];
    const h = len(rel) - (b ? b.radius : 0);
    return { body: bi, h, pos, rel, axes, local, tile };
  }

  /** The body whose surface is nearest to `pos` (relative to the home centre), with hysteresis against `current`: the picture follows it. Stars are not candidates. */
  nearest(pos: ArrayLike<number>, current: number): number {
    let best = -1, bd = Infinity;
    for (let i = 0; i < this.list.length; i++) {
      const b = this.list[i];
      if (b.kind === 'star') continue;
      const d = Math.max(len(sub(pos, b.pos)) - b.radius, 1);
      if (d < bd) { bd = d; best = i; }
    }
    if (best < 0) return current;
    const cb = this.list[current];
    if (cb && cb.kind !== 'star' && best !== current) {
      const dc = Math.max(len(sub(pos, cb.pos)) - cb.radius, 1);
      if (bd > SWITCH_RATIO * dc) return current;
    }
    return best;
  }

  /**
   * The stars' view: the observer's absolute universe position (units) and the local frame (east, north, up in universe space).
   */
  starView(p: Pose): { worldPos: V3; frame: M3 } {
    return { worldPos: [this.worldAbs[0] + (p.pos[0] - this.world[0]) / this.unitM, this.worldAbs[1] + (p.pos[1] - this.world[1]) / this.unitM, this.worldAbs[2] + (p.pos[2] - this.world[2]) / this.unitM], frame: p.local };
  }

  /** Where a universe direction is in render axes. */
  toRender(p: Pose, v: ArrayLike<number>): V3 { return mulv(p.axes, v); }

  /** The light on a point (relative to the home centre): the strongest star's direction (unit, toward it, universe axes) and flux per channel (world flux units). */
  lightAt(pos: ArrayLike<number>): { dir: V3; flux: V3; star: number } | null {
    let best: { dir: V3; flux: V3; star: number } | null = null, bl = -1;
    for (let i = 0; i < this.list.length; i++) {
      const s = this.list[i];
      if (s.kind !== 'star' || !(s.lum > 0)) continue;
      const d = sub(s.pos, pos), dm = len(d) / this.unitM;
      const f = s.lum / Math.max(dm * dm, 1e-12);
      if (f > bl) { bl = f; best = { dir: norm(d), flux: [f * s.rgb[0], f * s.rgb[1], f * s.rgb[2]], star: i }; }
    }
    return best;
  }

  /** The far shell's bodies for this pose: every body that is not a star, in render axes relative to the observer. */
  farBodies(p: Pose): FarBody[] {
    const out: FarBody[] = [];
    for (let i = 0; i < this.list.length; i++) {
      const b = this.list[i];
      if (b.kind === 'star') continue;
      const rel = sub(b.pos, p.pos);
      const dist = len(rel);
      const L = this.lightAt(b.pos);
      const ld = L ? this.toRender(p, L.dir) : [0, 1, 0] as V3;
      const flux: V3 = L ? L.flux : [0, 0, 0];
      // phase angle at the body: star - body - observer
      const toObs = norm(scale(rel, -1));
      const cosA = L ? Math.min(1, Math.max(-1, dot(L.dir, toObs))) : 1;
      const alpha = Math.acos(cosA);
      const phi = lambertPhase(alpha);
      const mean: V3 = [flux[0] * b.rgb[0] * (2 / 3) * phi, flux[1] * b.rgb[1] * (2 / 3) * phi, flux[2] * b.rgb[2] * (2 / 3) * phi];
      const fr = b.fixed;
      const fixed = rows(mulv(p.axes, [fr[0], fr[1], fr[2]]), mulv(p.axes, [fr[3], fr[4], fr[5]]), mulv(p.axes, [fr[6], fr[7], fr[8]]));
      out.push({ id: b.id, kind: b.kind, c: this.toRender(p, rel), r: b.radius, lightDir: ld, flux, fixed, mean, meanShade: b.kind === 'rock' ? meanShade(alpha) : 1, phase: alpha, ang: Math.asin(Math.min(1, b.radius / Math.max(dist, b.radius))), dist });
    }
    return out;
  }

  /** The star the picture's light is taken from, as the sky sees it from `p`: direction in render axes (unit), its distance (m) and angular radius (rad). */
  sunSeen(p: Pose): { dir: V3; dist: number; angR: number } | null {
    const L = this.lightAt(p.pos);
    if (!L) return null;
    const s = this.list[L.star];
    const rel = sub(s.pos, p.pos), dist = len(rel);
    return { dir: this.toRender(p, norm(rel)), dist, angR: Math.asin(Math.min(1, s.radius / dist)) };
  }

  /** The moon as the sky sees it from `p` (null when there is none): direction in render axes, angular radius. */
  moonSeen(p: Pose): { dir: V3; dist: number; angR: number; phase: number; mean: V3 } | null {
    const m = this.get('moon');
    if (!m) return null;
    const fb = this.farBodies(p).find(b => b.id === 'moon');
    if (!fb) return null;
    return { dir: norm(fb.c), dist: fb.dist, angR: fb.ang, phase: fb.phase, mean: fb.mean };
  }

  /**
   * How much of the star's disc the nearest world hides from `p` (0 to 1): the overlap of two discs on the sky (the world's angular radius against the star's). A fade for the
   * sun's disc and glare, which are the sky's own drawings and have no way to be cut by a limb.
   */
  starHidden(p: Pose): number {
    const s = this.sunSeen(p);
    if (!s) return 0;
    let hid = 0;
    for (const b of this.farBodies(p)) {
      if (b.kind !== 'world' && b.kind !== 'rock') continue;
      const sd = norm(b.c);
      const sep = Math.acos(Math.min(1, Math.max(-1, dot(sd, s.dir))));
      hid = Math.max(hid, discOverlap(b.ang, s.angR, sep));
    }
    return hid;
  }

  /** True when the observer is in the shadow of a body from the star (the ray to it meets a sphere). */
  inShadow(p: Pose): boolean {
    const L = this.lightAt(p.pos);
    if (!L) return false;
    for (let i = 0; i < this.list.length; i++) {
      const b = this.list[i];
      if (b.kind === 'star') continue;
      const oc = sub(p.pos, b.pos), bq = dot(oc, L.dir), cq = dot(oc, oc) - b.radius * b.radius;
      const disc = bq * bq - cq;
      if (disc > 0 && -bq + Math.sqrt(disc) > 0 && cq > 0 && -bq - Math.sqrt(disc) > 0) return true;
    }
    return false;
  }
}

/** The share of a disc of angular radius `rs` hidden by a disc of radius `rb`, their centres `sep` apart (all radians, small-angle plane geometry). */
export function discOverlap(rb: number, rs: number, sep: number): number {
  if (rs <= 0) return 0;
  if (sep >= rb + rs) return 0;
  if (sep <= rb - rs) return 1;
  if (sep <= rs - rb) return Math.min(1, (rb * rb) / (rs * rs));
  const a1 = Math.acos(Math.min(1, Math.max(-1, (sep * sep + rs * rs - rb * rb) / (2 * sep * rs))));
  const a2 = Math.acos(Math.min(1, Math.max(-1, (sep * sep + rb * rb - rs * rs) / (2 * sep * rb))));
  const area = rs * rs * a1 + rb * rb * a2 - 0.5 * Math.sqrt(Math.max(0, (-sep + rs + rb) * (sep + rs - rb) * (sep - rs + rb) * (sep + rs + rb)));
  return Math.min(1, Math.max(0, area / (Math.PI * rs * rs)));
}

/**
 * Re-express a view direction when the axes change (a new regime, a new followed body): the same direction in the universe, the new yaw and pitch (camera.rotation 'YXZ', no roll).
 */
export function rebaseView(oldAxes: ArrayLike<number>, newAxes: ArrayLike<number>, yaw: number, pitch: number): { yaw: number; pitch: number } {
  const f: V3 = [-Math.sin(yaw) * Math.cos(pitch), Math.sin(pitch), -Math.cos(yaw) * Math.cos(pitch)];
  const fu = mulvT(oldAxes, f);
  const fn = mulv(newAxes, fu);
  return { yaw: Math.atan2(-fn[0], -fn[2]), pitch: Math.asin(Math.min(1, Math.max(-1, fn[1]))) };
}

/**
 * Re-express the camera's whole attitude when the axes change: the same direction looked in AND the same way up, as seen from the universe, in the new axes (camera.rotation 'YXZ': yaw,
 * pitch, roll). rebaseView keeps only the direction; the way up is then the new axes' up, which is a different vector (the other side of a moon, a new regime) and turns the camera over.
 * Near straight up or down the yaw and the roll are not separate; the pair that reproduces the attitude is returned.
 */
export function rebaseAttitude(oldAxes: ArrayLike<number>, newAxes: ArrayLike<number>, yaw: number, pitch: number, roll: number): { yaw: number; pitch: number; roll: number } {
  // Q = Ry(yaw) Rx(pitch) Rz(roll), row-major (three's Euler 'YXZ')
  const a = Math.cos(pitch), b = Math.sin(pitch), c = Math.cos(yaw), d = Math.sin(yaw), e = Math.cos(roll), f = Math.sin(roll);
  const ce = c * e, cf = c * f, de = d * e, df = d * f;
  const Q = [ce + df * b, de * b - cf, a * d,
             a * f,       a * e,       -b,
             cf * b - de, df + ce * b, a * c];
  // Q' = A' A^T Q  (A rows: render axes in universe axes)
  const T = [0, 0, 0, 0, 0, 0, 0, 0, 0];       // A^T Q: the attitude in universe axes
  for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) T[i * 3 + j] = oldAxes[0 * 3 + i] * Q[0 * 3 + j] + oldAxes[1 * 3 + i] * Q[1 * 3 + j] + oldAxes[2 * 3 + i] * Q[2 * 3 + j];
  const N = [0, 0, 0, 0, 0, 0, 0, 0, 0];      // A' T
  for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) N[i * 3 + j] = newAxes[i * 3 + 0] * T[0 * 3 + j] + newAxes[i * 3 + 1] * T[1 * 3 + j] + newAxes[i * 3 + 2] * T[2 * 3 + j];
  const m12 = N[5];
  const p = Math.asin(Math.min(1, Math.max(-1, -m12)));
  if (Math.abs(m12) < 0.9999999) return { yaw: Math.atan2(N[2], N[8]), pitch: p, roll: Math.atan2(N[3], N[4]) };
  return { yaw: Math.atan2(-N[6], N[0]), pitch: p, roll: 0 };
}

/** A vector in render axes (a displacement the controls made) as a vector in universe axes. */
export function renderToUniverse(axes: ArrayLike<number>, v: ArrayLike<number>): V3 { return mulvT(axes, v); }
